import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import Header from '@/components/layout/Header';
import MobileNav from '@/components/layout/MobileNav';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Plus, FolderOpen, Image, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import AlbumCover from '@/components/albums/AlbumCover';
import SmartCollections from '@/components/albums/SmartCollections';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function AlbumsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const canCreate = user?.role === 'admin' || user?.role === 'member';
  const [albums, setAlbums] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [albumName, setAlbumName] = useState('');
  const [albumDescription, setAlbumDescription] = useState('');
  const [creating, setCreating] = useState(false);

  const fetchAlbums = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/albums`, { withCredentials: true });
      setAlbums(response.data);
    } catch (error) {
      console.error('Failed to fetch albums:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAlbums();
  }, [fetchAlbums]);

  const handleCreateAlbum = async (e) => {
    e.preventDefault();
    if (!albumName.trim()) return;

    setCreating(true);
    try {
      const response = await axios.post(`${API}/albums`, {
        name: albumName.trim(),
        description: albumDescription.trim()
      }, { withCredentials: true });
      
      setAlbums(prev => [...prev, response.data]);
      setAlbumName('');
      setAlbumDescription('');
      setCreateDialogOpen(false);
      toast.success('Album created!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create album');
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7] pb-20 md:pb-8" data-testid="albums-page">
      <Header />
      
      <main className="max-w-6xl mx-auto px-4 py-6">
        <SmartCollections />
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold text-stone-900">Photo Albums</h1>
          {canCreate && (
            <Button
              onClick={() => setCreateDialogOpen(true)}
              className="bg-teal-600 hover:bg-teal-700 text-white rounded-full"
              data-testid="create-album-btn"
            >
              <Plus className="w-5 h-5 mr-2" />
              New Album
            </Button>
          )}
        </div>

        {loading ? (
          <div className="text-center py-12">
            <Loader2 className="w-8 h-8 text-teal-600 animate-spin mx-auto mb-4" />
            <p className="text-stone-500">Loading albums...</p>
          </div>
        ) : albums.length === 0 ? (
          <div className="card-soft text-center py-12" data-testid="empty-albums">
            <div className="w-16 h-16 bg-teal-50 rounded-full flex items-center justify-center mx-auto mb-4">
              <FolderOpen className="w-8 h-8 text-teal-600" />
            </div>
            <h2 className="text-xl font-semibold text-stone-900 mb-2">No albums yet</h2>
            <p className="text-stone-500 mb-6">
              {canCreate ? 'Create your first album to organize family photos' : 'Albums will appear here once created by family members.'}
            </p>
            {canCreate && (
              <Button
                onClick={() => setCreateDialogOpen(true)}
                className="bg-teal-600 hover:bg-teal-700 text-white rounded-full"
              >
                <Plus className="w-5 h-5 mr-2" />
                Create Album
              </Button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {albums.map(album => (
              <div
                key={album.id}
                onClick={() => navigate(`/albums/${album.id}`)}
                className="cursor-pointer group"
                data-testid={`album-${album.id}`}
              >
                <div className="aspect-square rounded-2xl overflow-hidden bg-stone-100 mb-3 relative">
                  {album.cover_url ? (
                    <AlbumCover fileId={album.cover_url} />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Image className="w-12 h-12 text-stone-300" />
                    </div>
                  )}
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors" />
                </div>
                <h3 className="font-semibold text-stone-900 truncate">{album.name}</h3>
                <p className="text-sm text-stone-500">{album.photo_count} photos</p>
              </div>
            ))}
          </div>
        )}
      </main>

      <MobileNav />

      {/* Create Album Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent className="max-w-md rounded-3xl" data-testid="create-album-dialog">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-stone-900">Create New Album</DialogTitle>
          </DialogHeader>

          <form onSubmit={handleCreateAlbum} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="albumName" className="text-stone-700">Album Name</Label>
              <Input
                id="albumName"
                value={albumName}
                onChange={(e) => setAlbumName(e.target.value)}
                placeholder="e.g., Summer Vacation 2024"
                className="rounded-xl border-stone-200 focus:border-teal-500 focus:ring-teal-500"
                required
                data-testid="album-name-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="albumDescription" className="text-stone-700">Description (optional)</Label>
              <Textarea
                id="albumDescription"
                value={albumDescription}
                onChange={(e) => setAlbumDescription(e.target.value)}
                placeholder="What's this album about?"
                className="rounded-xl border-stone-200 focus:border-teal-500 focus:ring-teal-500 resize-none"
                rows={3}
                data-testid="album-description-input"
              />
            </div>

            <div className="flex gap-3 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setCreateDialogOpen(false)}
                className="flex-1 rounded-full"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={creating || !albumName.trim()}
                className="flex-1 bg-teal-600 hover:bg-teal-700 text-white rounded-full"
                data-testid="submit-album-btn"
              >
                {creating ? 'Creating...' : 'Create Album'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
